import type { Position } from "geojson";
import {
	addPoint,
	deletePoint,
	movePoint,
	moveSampleWithCores,
	renamePoint,
	updateProperties,
} from "../../../domain/sample/sampleService";
import type { SamplePointCollection, SamplePointProperties, CoreCollection } from "../../../domain/sample/types";
import type { GeometryResult } from "../../../domain/geometry/types";
import type { UndoableCommand } from "../undoStack";

type ApplyFn = (col: SamplePointCollection) => void;

export interface AddPointResult {
	command: UndoableCommand;
	collection: SamplePointCollection;
}

export function addPointUseCase(
	col: SamplePointCollection,
	sampleId: string,
	coords: Position,
	apply: ApplyFn,
	extra?: Partial<Omit<SamplePointProperties, "sample_id">>,
): GeometryResult<AddPointResult> {
	const result = addPoint(col, sampleId, coords, extra);
	if (!result.ok) return result;

	const newCol = result.data;
	return {
		ok: true,
		data: {
			collection: newCol,
			command: {
				label: `Add point ${sampleId}`,
				execute: () => apply(newCol),
				undo: () => apply(col),
			},
		},
	};
}

export interface DeletePointResult {
	command: UndoableCommand;
	collection: SamplePointCollection;
}

export function deletePointUseCase(
	col: SamplePointCollection,
	sampleId: string,
	apply: ApplyFn,
): GeometryResult<DeletePointResult> {
	const result = deletePoint(col, sampleId);
	if (!result.ok) return result;

	const newCol = result.data;
	return {
		ok: true,
		data: {
			collection: newCol,
			command: {
				label: `Delete point ${sampleId}`,
				execute: () => apply(newCol),
				undo: () => apply(col),
			},
		},
	};
}

export interface RenamePointResult {
	command: UndoableCommand;
	collection: SamplePointCollection;
}

export function renamePointUseCase(
	col: SamplePointCollection,
	oldId: string,
	newId: string,
	apply: ApplyFn,
): GeometryResult<RenamePointResult> {
	const result = renamePoint(col, oldId, newId);
	if (!result.ok) return result;

	const newCol = result.data;
	return {
		ok: true,
		data: {
			collection: newCol,
			command: {
				label: `Rename point ${oldId} → ${newId}`,
				execute: () => apply(newCol),
				undo: () => apply(col),
			},
		},
	};
}

export interface MovePointResult {
	command: UndoableCommand;
	collection: SamplePointCollection;
}

export function movePointUseCase(
	col: SamplePointCollection,
	sampleId: string,
	newCoords: Position,
	apply: ApplyFn,
): GeometryResult<MovePointResult> {
	const result = movePoint(col, sampleId, newCoords);
	if (!result.ok) return result;

	const newCol = result.data;
	return {
		ok: true,
		data: {
			collection: newCol,
			command: {
				label: `Move point ${sampleId}`,
				execute: () => apply(newCol),
				undo: () => apply(col),
			},
		},
	};
}

export interface UpdatePointPropsResult {
	command: UndoableCommand;
	collection: SamplePointCollection;
}

export function updatePointPropsUseCase(
	col: SamplePointCollection,
	sampleId: string,
	props: Partial<Omit<SamplePointProperties, "sample_id">>,
	apply: ApplyFn,
): GeometryResult<UpdatePointPropsResult> {
	const result = updateProperties(col, sampleId, props);
	if (!result.ok) return result;

	const newCol = result.data;
	return {
		ok: true,
		data: {
			collection: newCol,
			command: {
				label: `Update point ${sampleId}`,
				execute: () => apply(newCol),
				undo: () => apply(col),
			},
		},
	};
}

export interface MoveSampleWithCoresResult {
	command: UndoableCommand;
	points: SamplePointCollection;
	cores: CoreCollection;
}

export function moveSampleWithCoresUseCase(
	points: SamplePointCollection,
	cores: CoreCollection,
	sampleId: string,
	newCoords: Position,
	applyPoints: ApplyFn,
	applyCores: (col: CoreCollection) => void,
): GeometryResult<MoveSampleWithCoresResult> {
	const result = moveSampleWithCores(points, cores, sampleId, newCoords);
	if (!result.ok) return result;

	const newPoints = result.data.points;
	const newCores = result.data.cores;
	return {
		ok: true,
		data: {
			points: newPoints,
			cores: newCores,
			command: {
				label: `Move sample ${sampleId} with cores`,
				execute: () => {
					applyPoints(newPoints);
					applyCores(newCores);
				},
				undo: () => {
					applyPoints(points);
					applyCores(cores);
				},
			},
		},
	};
}
